import { useContext } from 'react'
import { StaticDataImporterContext } from '../context/StaticDataImporterContext'
import { TemporaryDataType } from '../temporary-data-table/useTemporaryDataState'

import _ from 'lodash'

interface SelectAllRowsProps {
    type: keyof TemporaryDataType
    data: TemporaryDataType
    selectedRows: Entity
    parentNodeId: number | number[]
    target?: EntityDto
    selected?: boolean
    nativeEvent?: Event
}

const exceptions = {
    interventions: ['wells'],
    rigTables: [
        'wells',
        'interventions',
        'sections',
        'runs',
        'tubulars',
        'cements',
        'pipeMovements',
        'connections',
        'rops'
    ],
    sections: ['wells', 'interventions', 'rigTables'],
    runs: ['wells', 'interventions', 'sections', 'rigTables'],
    tubulars: ['wells', 'interventions', 'sections', 'runs', 'rigTables', 'cements'],
    cements: ['wells', 'interventions', 'sections', 'runs', 'tubulars', 'rigTables'],
    pipeMovements: ['wells', 'interventions', 'sections', 'runs', 'tubulars', 'cements', 'rigTables'],
    connections: ['wells', 'interventions', 'sections', 'runs', 'tubulars', 'cements', 'rigTables'],
    rops: ['wells', 'interventions', 'sections', 'runs', 'tubulars', 'cements', 'rigTables']
}

export function useSelectAllRows(): {
    selectAllRows: (props: SelectAllRowsProps) => void
} {
    const {
        setCheckedRows,
        checkedRows,
        formatCheckedRows,
        setIsValidToTransfer,
        handleCheckedToTransfer
    } = useContext(StaticDataImporterContext)

    function save(newData: Record<string, Entity>): void {
        const newCheckedToTransfer = removeDuplicates({ ...checkedRows, ...newData })
        formatCheckedRows(newCheckedToTransfer)
        setCheckedRows(newCheckedToTransfer)
        setIsValidToTransfer(false)
    }

    let deselectRef = ''

    function selectAllRows({ data, type, selectedRows, parentNodeId, target, selected }: SelectAllRowsProps): void {
        const action: 'select' | 'deselect' = (target ? selected : selectedRows?.length) ? 'select' : 'deselect'

        const selectedParent = target
            ? String(target?.parent)
            : typeof parentNodeId === 'number'
            ? String(parentNodeId)
            : parentNodeId?.map(id => String(id))

        const allRowsFromDataWithTheSameParent = getAllRowsWithSameParent(data, type, selectedParent)

        const isSelectAll = allRowsFromDataWithTheSameParent?.length === selectedRows?.length || type === 'wells'

        const rowsSelected: Record<string, EntityInHierarchy[]> = {}

        let currentWell: WellDto

        const localCheckedRows = checkedRows

        if (action === 'deselect' && localCheckedRows) {
            deselectRef = 'deselect'
            deselect(
                data,
                selectedRows,
                target,
                currentWell,
                localCheckedRows,
                checkedRows,
                parentNodeId,
                selectedParent,
                type,
                save
            )

            return
        }

        // deselectRef is used to avoid calling select becasue exists two calls to selectAllRows
        if (action === 'select' && deselectRef !== 'deselect') {
            select(
                rowsSelected,
                isSelectAll,
                selectedRows,
                type,
                data,
                currentWell,
                parentNodeId,
                handleCheckedToTransfer,
                save
            )
        }
    }

    return { selectAllRows }
}

const select = (
    rowsSelected: Record<string, EntityInHierarchy[]>,
    isSelectAll: boolean,
    selectedRows: Entity,
    type: keyof TemporaryDataType,
    data: TemporaryDataType,
    currentWell: WellDto,
    parentNodeId: number | number[],
    handleCheckedToTransfer: (key: string, items: Entity) => void,
    save: (newData: Record<string, Entity>) => void
): void => {
    const addRows = (key: string, items: EntityInHierarchy[]): void => {
        if (!rowsSelected[key]) {
            rowsSelected[key] = []
        }
        rowsSelected[key].push(...items)
    }

    if (isSelectAll) {
        const wellsHierarchy = mountWellHierarchy(data)

        Object.keys(wellsHierarchy).forEach(key => {
            if (selectedRows?.find(row => String(row?.parent) === key)) {
                processData(wellsHierarchy[key], type, selectedRows, addRows)
            } else {
                const wellParentOfEntity = getWellFromFrontUuid(data, selectedRows[0])
                if (String(wellParentOfEntity.parent) === key) {
                    processData(wellsHierarchy[key], type, selectedRows, addRows)
                }
            }
        })
        save(rowsSelected as Record<string, Entity>)
    }

    if (!isSelectAll) {
        if (typeof parentNodeId === 'number') {
            const key = `${type}=>${type === 'wells' ? currentWell?.nodeId : parentNodeId}`
            handleCheckedToTransfer(key, selectedRows as Entity)
        }

        if (Array.isArray(parentNodeId)) {
            parentNodeId.forEach(id => {
                const key = `${type}=>${id}`
                handleCheckedToTransfer(key, selectedRows as Entity)
            })
        }
    }
}

const deselect = (
    data: TemporaryDataType,
    selectedRows: Entity,
    target: EntityDto,
    currentWell: WellDto,
    localCheckedRows: Record<string, Entity>,
    checkedRows: Record<string, Entity>,
    parentNodeId: number | number[],
    selectedParent: string | string[],
    type: keyof TemporaryDataType,
    save: (newData: Record<string, Entity>) => void
): void => {
    const separatedCheckedRowsByWells = separateCheckedRowsByWells(checkedRows, data)
    const cleanedRows = {}

    if (target || selectedRows?.length) {
        const well = getWellFromFrontUuid(data, target || selectedRows[0])
        currentWell = well
        const checkedRowsByTargetWell = separatedCheckedRowsByWells[`wells=>${well.nodeId}`]
        localCheckedRows = checkedRowsByTargetWell
    }

    if ((!target || !selectedRows?.length) && parentNodeId) {
        const entity = Object.values(data)
            ?.flat()
            ?.find(row =>
                typeof parentNodeId === 'number' ? row?.nodeId === parentNodeId : parentNodeId.includes(row?.nodeId)
            )
        if (entity?.parent) {
            const well = (isWellEntity(entity) ? entity : getWellFromFrontUuid(data, entity)) as WellDto
            currentWell = well
            const checkedRowsByTargetWell = separatedCheckedRowsByWells[`wells=>${well.nodeId}`]
            localCheckedRows = checkedRowsByTargetWell
        }
    }
    for (const key of Object.keys(localCheckedRows)) {
        delete cleanedRows[key]
        const shouldCleanup = !exceptions[type]?.includes(key.split('=>')[0])

        const avoidCleanBrother = key.includes(type)
            ? key.includes(String(selectedParent)) || key.includes(String(parentNodeId))
            : true

        if (shouldCleanup && avoidCleanBrother) {
            cleanedRows[key] = []
        }
    }

    save(cleanedRows)
}

export const getAllRowsWithSameParent = (
    data: TemporaryDataType,
    type: keyof TemporaryDataType,
    selectedParent: string | string[]
): EntityInHierarchy[] =>
    Object.keys(data).reduce((acc, key) => {
        if (key === type && data[key]) {
            const rows = data[key]?.filter(row =>
                typeof selectedParent === 'string'
                    ? String(row?.parent) === selectedParent
                    : selectedParent.includes(String(row?.parent))
            )

            return [...acc, ...rows]
        }

        return acc
    }, [])

export const getSelectedRowKeys = (
    checkedRows: Record<string, Entity>,
    type: keyof TemporaryDataType,
    parent: EntityDto
): string[] => {
    if (!checkedRows || !type || !parent) return []

    const rows = checkedRows?.[`${type}=>${parent?.nodeId}`]

    let keys = []

    if (rows) {
        // Is necessary to convert the keys to string because the keys are stored as strings passed in the dataSource property
        keys = rows.map((row: EntityDto) => String(row?.key))
    }

    return keys
}

export const mountWellHierarchy = (data: TemporaryDataType): WellHieararchy => {
    const wellsHierarchy = {} as WellHieararchy

    for (const well of data.wells) {
        const wellKey = `${well.parent}`
        wellsHierarchy[wellKey] = well
        wellsHierarchy[wellKey].interventions = data.interventions.filter(
            intervention => intervention.parent === well.nodeId
        )
        wellsHierarchy[wellKey].interventions.forEach(intervention => {
            intervention.sections = data.sections?.filter(section => section.parent === intervention.nodeId)
            intervention.sections.forEach(section => {
                section.runs = data.runs?.filter(run => run.parent === section.nodeId)
                section.runs.forEach(run => {
                    run.tubulars = data.tubulars?.filter(tubular => tubular.parent === run.nodeId)
                    run.cements = data.cements?.filter(cement => cement.parent === run.nodeId)
                    run.tubulars.forEach(tubular => {
                        tubular.pipeMovements = data.pipeMovements?.filter(
                            pipeMovement => pipeMovement.parent === tubular.nodeId
                        )
                        tubular.connections = data.connections?.filter(
                            connection => connection.parent === tubular.nodeId
                        )
                        tubular.rops = data.rops?.filter(rop => rop.parent === tubular.nodeId)
                    })
                })
            })
            intervention.rigTables = data.rigTables?.filter(rigTable => rigTable.parent === intervention.nodeId)
        })
    }

    return wellsHierarchy
}

export const separateCheckedRowsByWells = (
    checkedRows: Record<string, Entity>,
    data: TemporaryDataType
): Record<string, Record<string, Entity>> => {
    if (!checkedRows) return

    const hierarchyByWells = data.wells?.reduce((acc, row) => {
        const well = getWellFromFrontUuid(data, row)
        const wellKey = `wells=>${well.nodeId}`
        if (!acc[wellKey]) {
            acc[wellKey] = {}
        }

        return acc
    }, {} as Record<string, Record<string, Entity>>)

    Object.keys(checkedRows).forEach(key => {
        const entity = checkedRows[key]
        const well = getWellFromFrontUuid(data, entity[0])
        const wellKey = `wells=>${well.nodeId}`

        if (hierarchyByWells[wellKey]) {
            hierarchyByWells[wellKey] = { ...hierarchyByWells[wellKey], [key]: entity }
        }
    })

    return hierarchyByWells
}

export const addRowsAvoidingSelectedBrothers = (
    type: keyof TemporaryDataType,
    targetType: keyof TemporaryDataType,
    selectedRows: Entity,
    entity:
        | WellHieararchy
        | InterventionsHieararchy
        | SectionHieararchy
        | RigTableHieararchy
        | RunHieararchy
        | TubularTypeHieararchy
        | CementedPlugHieararchy
        | TubularRestrictedPipeMovementsDto
        | TubularRestrictedConnectionDto
        | TubularRestrictedRopDto,
    addRows: (key: string, items: EntityInHierarchy[]) => void
): void => {
    if (type === targetType) {
        if (selectedRows?.some(row => row?.nodeId === entity?.nodeId)) {
            addRows(`${type}=>${entity.parent}`, [entity])
        }
    } else {
        addRows(`${targetType}=>${entity.parent}`, [entity])
    }
}

export const processData = (
    well: WellHieararchy,
    type: keyof TemporaryDataType,
    selectedRows: Entity,
    addRows: (key: string, items: EntityInHierarchy[]) => void
): void => {
    if (!well) return

    if (type === 'wells') {
        addRowsAvoidingSelectedBrothers(type, 'wells', selectedRows, well, addRows)
    }

    well?.interventions?.forEach(intervention => {
        if (type === 'wells' || type === 'interventions') {
            addRowsAvoidingSelectedBrothers(type, 'interventions', selectedRows, intervention, addRows)
        }
        intervention?.sections?.forEach(section => {
            if (type === 'wells' || type === 'interventions' || type === 'sections') {
                addRowsAvoidingSelectedBrothers(type, 'sections', selectedRows, section, addRows)
            }

            section?.runs?.forEach(run => {
                if (type === 'wells' || type === 'interventions' || type === 'sections' || type === 'runs') {
                    addRowsAvoidingSelectedBrothers(type, 'runs', selectedRows, run, addRows)
                }

                run?.tubulars?.forEach(tubular => {
                    if (
                        type === 'wells' ||
                        type === 'interventions' ||
                        type === 'sections' ||
                        type === 'runs' ||
                        type === 'tubulars'
                    ) {
                        addRowsAvoidingSelectedBrothers(type, 'tubulars', selectedRows, tubular, addRows)
                    }

                    tubular?.pipeMovements?.forEach(pipeMovement => {
                        if (
                            type === 'wells' ||
                            type === 'interventions' ||
                            type === 'sections' ||
                            type === 'runs' ||
                            type === 'tubulars' ||
                            type === 'pipeMovements'
                        ) {
                            addRowsAvoidingSelectedBrothers(type, 'pipeMovements', selectedRows, pipeMovement, addRows)
                        }
                    })
                    tubular?.connections?.forEach(connection => {
                        if (
                            type === 'wells' ||
                            type === 'interventions' ||
                            type === 'sections' ||
                            type === 'runs' ||
                            type === 'tubulars' ||
                            type === 'connections'
                        ) {
                            addRowsAvoidingSelectedBrothers(type, 'connections', selectedRows, connection, addRows)
                        }
                    })
                    tubular?.rops?.forEach(rop => {
                        if (
                            type === 'wells' ||
                            type === 'interventions' ||
                            type === 'sections' ||
                            type === 'runs' ||
                            type === 'tubulars' ||
                            type === 'rops'
                        ) {
                            addRowsAvoidingSelectedBrothers(type, 'rops', selectedRows, rop, addRows)
                        }
                    })
                })

                run?.cements?.forEach(cement => {
                    if (
                        type === 'wells' ||
                        type === 'interventions' ||
                        type === 'sections' ||
                        type === 'runs' ||
                        type === 'cements'
                    ) {
                        addRowsAvoidingSelectedBrothers(type, 'cements', selectedRows, cement, addRows)
                    }
                })
            })
        })
        intervention?.rigTables?.forEach(rigTable => {
            if (type === 'wells' || type === 'interventions' || type === 'rigTables') {
                addRowsAvoidingSelectedBrothers(type, 'rigTables', selectedRows, rigTable, addRows)
            }
        })
    })
}

export const removeDuplicates = (data: Record<string, Entity>): Record<string, Entity> => {
    const uniqueData: Record<string, Entity> = {}

    for (const key in data) {
        const entity = data[key]

        if (!entity || !entity?.length) continue

        const uniqueEntity = entity?.filter((item, index) => {
            return entity?.findIndex(e => e?.frontUuid === item?.frontUuid) === index
        }) as Entity

        uniqueData[key] = uniqueEntity
    }

    return uniqueData
}

export const isWellEntity = (entity: EntityDto): boolean => {
    if (!entity) return false

    return (
        _.has(entity, 'wellCategory') ||
        _.has(entity, 'wellType') ||
        _.has(entity, 'wellId') ||
        _.has(entity, 'wellName') ||
        _.has(entity, 'wellIdInCodaf')
    )
}

export const getWellFromFrontUuid = (data: TemporaryDataType, entity: EntityDto): WellDto => {
    const findParentEntity = (data: TemporaryDataType, parentId: string | number): WellDto | undefined => {
        for (const key in data) {
            const entities = data[key]
            if (entities) {
                for (const entity of entities) {
                    if (entity.nodeId === parentId) {
                        return entity as WellDto
                    }
                }
            }
        }

        return undefined
    }

    if (isWellEntity(entity)) {
        return entity as WellDto
    }

    let parentEntity = findParentEntity(data, entity.parent)
    while (!isWellEntity(parentEntity)) {
        parentEntity = findParentEntity(data, parentEntity.parent)
    }

    if (parentEntity) {
        return parentEntity
    }

    return entity as WellDto
}
